import React, { useRef, useState, useEffect, useCallback } from 'react';
import { X, Camera, RefreshCw } from 'lucide-react';

/**
 * CameraCapture — Komponen untuk menangkap foto dari kamera HP/Webcam
 * Menggunakan getUserMedia untuk preview langsung + capture ke canvas
 * 
 * Props:
 * @param {boolean} show - Tampilkan/sembunyikan kamera
 * @param {function} onCapture(base64DataUrl) - Callback saat foto diambil
 * @param {function} onClose - Callback saat ditutup
 * @param {'user'|'environment'} facingMode - Arah kamera (default: 'user' utk selfie)
 */
export default function CameraCapture({ show, onCapture, onClose, facingMode = 'user' }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [error, setError] = useState('');
  const [initializing, setInitializing] = useState(false);

  const initCamera = useCallback(async () => {
    setError('');
    setCapturedPhoto(null);
    setInitializing(true);

    try {
      // Hentikan stream sebelumnya jika ada
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }

      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 640 },
          height: { ideal: 480 }
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      console.error('[Camera] Error initializing camera:', err);
      if (err.name === 'NotAllowedError') {
        setError('Izin kamera ditolak. Izinkan akses kamera di pengaturan browser.');
      } else if (err.name === 'NotFoundError') {
        setError('Kamera tidak ditemukan di perangkat ini.');
      } else if (err.name === 'NotReadableError') {
        setError('Kamera sedang digunakan oleh aplikasi lain.');
      } else {
        setError('Gagal mengakses kamera: ' + err.message);
      }
    } finally {
      setInitializing(false);
    }
  }, [facingMode]);

  const mountTimerRef = useRef(null);

  useEffect(() => {
    if (show) {
      mountTimerRef.current = setTimeout(() => initCamera(), 300);
    }
    return () => {
      if (mountTimerRef.current) {
        clearTimeout(mountTimerRef.current);
        mountTimerRef.current = null;
      }
      // Bersihkan stream saat komponen unmount atau show berubah
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, [show, initCamera]);

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    // Mirror for selfie (facingMode === 'user')
    if (facingMode === 'user') {
      ctx.translate(width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, width, height);

    // Reset transform
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    // Compress to JPEG quality 0.7
    const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
    setCapturedPhoto(dataUrl);
  };

  const retake = () => {
    setCapturedPhoto(null);
    initCamera();
  };

  const confirmPhoto = () => {
    if (capturedPhoto && onCapture) {
      onCapture(capturedPhoto);
      // Cleanup
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    }
  };

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1100 }}>
      <div
        className="modal-content"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: 420, padding: 0, overflow: 'hidden' }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 16px', borderBottom: '1px solid var(--gray-100)'
        }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--gray-700)', margin: 0 }}>
            <Camera size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            {facingMode === 'user' ? 'Selfie Absensi' : 'Ambil Foto'}
          </h3>
          <button className="btn-icon" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Camera View */}
        <div style={{ position: 'relative', background: '#000', minHeight: 300 }}>
          {initializing && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
              justifyContent: 'center', color: '#fff', background: 'rgba(0,0,0,0.5)', zIndex: 2
            }}>
              <div style={{ textAlign: 'center' }}>
                <div className="spinner-lg" style={{ borderTopColor: '#11cdef', margin: '0 auto 12px' }} />
                <span style={{ fontSize: '0.85rem' }}>Mengakses kamera...</span>
              </div>
            </div>
          )}

          {error && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
              justifyContent: 'center', color: '#fff', padding: 20, background: 'rgba(0,0,0,0.7)', zIndex: 2
            }}>
              <div style={{ textAlign: 'center', maxWidth: 300 }}>
                <Camera size={40} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
                <p style={{ fontSize: '0.85rem', color: '#f87171', marginBottom: 12 }}>{error}</p>
                <button className="btn btn-primary" onClick={initCamera} style={{ fontSize: '0.8rem' }}>
                  <RefreshCw size={14} /> Coba Lagi
                </button>
              </div>
            </div>
          )}

          {/* Video Preview */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              maxHeight: 420,
              objectFit: 'contain',
              display: capturedPhoto ? 'none' : 'block',
              transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
              background: '#000'
            }}
          />

          {/* Captured Photo Preview */}
          {capturedPhoto && (
            <img
              src={capturedPhoto}
              alt="Preview"
              style={{ width: '100%', maxHeight: 420, objectFit: 'contain', display: 'block', background: '#000' }}
            />
          )}

          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex', justifyContent: 'center', gap: 16,
          padding: '16px 20px', borderTop: '1px solid var(--gray-100)'
        }}>
          {!capturedPhoto ? (
            <button
              className="btn btn-primary"
              onClick={capture}
              disabled={initializing || !!error}
              style={{
                borderRadius: '50%', width: 56, height: 56, padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(17,205,239,0.4)',
                fontSize: '1.3rem'
              }}
            >
              <Camera size={24} />
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 12, width: '100%' }}>
              <button className="btn btn-secondary" onClick={retake} style={{ flex: 1 }}>
                <RefreshCw size={14} /> Ulang
              </button>
              <button className="btn btn-primary" onClick={confirmPhoto} style={{ flex: 1 }}>
                <Camera size={14} /> Gunakan Foto
              </button>
            </div>
          )}
        </div>

        {!capturedPhoto && !error && (
          <p style={{
            textAlign: 'center', fontSize: '0.75rem', color: 'var(--gray-400)',
            padding: '0 16px 14px', margin: 0
          }}>
            Pastikan wajah terlihat jelas dan pencahayaan cukup
          </p>
        )}
      </div>
    </div>
  );
}
